import type { FunctionArgs, ParameterArgs } from 'ncalcjs';
import { Expression, ValueExpression } from 'ncalcjs';

import type {
  EstimationModelItemParameterResponse,
  EstimationModelResponse,
  MethodologyResponse,
} from '@/api/schema/data-contracts';
import { EstimationCalculatorValidator } from '@/utils/calculations/EstimationCalculatorValidator.ts';

import type { EstimationItemForCalculation } from './model';

class ItemCalculationInformation {
  public formula: string | null = null;
  public parameters: EstimationModelItemParameterResponse[] = [];
}

export class EstimationCalculator {
  private estimationModel: EstimationModelResponse;
  private estimationMethodologies: MethodologyResponse[];

  private calculatedResultByEstimationItemId: Map<string, number> = new Map();
  private expressionByFormula: Map<string, Expression> = new Map();

  public constructor(
    estimationModel: EstimationModelResponse,
    estimationMethodologies: MethodologyResponse[],
  ) {
    this.estimationModel = estimationModel;
    this.estimationMethodologies = estimationMethodologies;
  }

  public recalculateEstimations(estimationItems: EstimationItemForCalculation[]) {
    // clear all values
    this.calculatedResultByEstimationItemId = new Map();
    this.expressionByFormula = new Map();

    EstimationCalculatorValidator.checkInvalidItems(estimationItems);
    const recursiveVisits = new Set<string>();

    // calculate
    estimationItems.forEach((estimationItem) => {
      this.calculateItemEstimation(estimationItem, estimationItems, recursiveVisits);
    });

    return this.calculatedResultByEstimationItemId;
  }

  private calculateItemEstimation(
    estimationItem: EstimationItemForCalculation,
    estimationItems: EstimationItemForCalculation[],
    recursiveVisits: Set<string>,
  ): number {
    if (estimationItem.isOmitted) return 0;

    if (!this.calculatedResultByEstimationItemId.has(estimationItem.id)) {
      const subItems = estimationItems.filter((i) => estimationItem.id === i.parentId);

      subItems.forEach((i) => {
        EstimationCalculatorValidator.checkEndlessLoop(i, estimationItems, recursiveVisits);
        recursiveVisits.add([estimationItem.parentId, estimationItem.id].join('-'));
      });

      if (subItems.length > 0) {
        if (!estimationItem.isParentBasedCalculation) {
          // calculate sum of sub-items
          const value = subItems.reduce((estimation, subItem) => {
            return (
              estimation + this.calculateItemEstimation(subItem, estimationItems, recursiveVisits)
            );
          }, 0);

          this.calculatedResultByEstimationItemId.set(estimationItem.id, +value.toFixed(2));
        } else {
          // otherwise calculate current item
          const value = this.calculateEstimationByFormula(
            estimationItem,
            estimationItems,
            recursiveVisits,
          );
          this.calculatedResultByEstimationItemId.set(estimationItem.id, +value.toFixed(2));
        }
      } else {
        // Items without sub-items should still be calculated
        const value = this.calculateEstimationByFormula(
          estimationItem,
          estimationItems,
          recursiveVisits,
        );
        this.calculatedResultByEstimationItemId.set(estimationItem.id, +value.toFixed(2));
      }
    }

    return this.calculatedResultByEstimationItemId.get(estimationItem.id) ?? 0;
  }

  private calculateEstimationByFormula(
    estimationItem: EstimationItemForCalculation,
    estimationItems: EstimationItemForCalculation[],
    recursiveVisits: Set<string>,
  ) {
    const calcInfo = this.getCalculationInformation(estimationItem);

    if (!calcInfo.formula) return 0;

    // Reuse parsed Expression when the formula matches — callbacks are always overwritten before Evaluate()
    let expression = this.expressionByFormula.get(calcInfo.formula);
    if (!expression) {
      expression = new Expression(calcInfo.formula);
      this.expressionByFormula.set(calcInfo.formula, expression);
    }

    // inject parameters
    calcInfo.parameters.forEach((p) => {
      expression.EvaluateParameter[`param[${p.calculationRef}]`] = (args: ParameterArgs) => {
        const paramValue = estimationItem.parameters.find(
          (estimationItemParameter) => estimationItemParameter.calculationRef === p.calculationRef,
        );
        args.Result = paramValue?.value ?? 0;
      };
    });

    // inject estimation items
    estimationItems.forEach((i) => {
      expression.EvaluateParameter[`item[${i.calculationRef}]`] = (args: ParameterArgs) => {
        args.Result = this.calculateItemEstimation(i, estimationItems, recursiveVisits);
      };
    });

    // additional functions
    expression.EvaluateFunction.sumItemsExcept = (args: FunctionArgs) => {
      const skipItems = args.Parameters.filter(
        (formulaParameter) => formulaParameter.ParsedExpression instanceof ValueExpression,
      ).map((p) => (<ValueExpression>p.ParsedExpression).Value);

      // Create a unique key for this sumItemsExcept call context
      const sumItemsExceptKey = `sumItemsExcept-${estimationItem.id}-${estimationItem.parentId}`;

      // Check if we're already in a sumItemsExcept call for this context
      if (recursiveVisits.has(sumItemsExceptKey)) {
        args.Result = 0; // Return 0 to break the recursion
        return;
      }

      // Add this sumItemsExcept call to recursive visits
      recursiveVisits.add(sumItemsExceptKey);

      try {
        args.Result = this.calculateSiblingsEstimations(
          estimationItem,
          estimationItems,
          skipItems,
          recursiveVisits,
        );
      } finally {
        // Always remove the sumItemsExcept call from recursive visits when done
        recursiveVisits.delete(sumItemsExceptKey);
      }
    };

    return expression.Evaluate() as number;
  }

  private getCalculationInformation(estimationItem: EstimationItemForCalculation) {
    const calcInfo = new ItemCalculationInformation();

    let modelItem;
    if (
      estimationItem.modelItemId &&
      (modelItem = this.estimationModel?.items.find(
        (mItem) => mItem.id === estimationItem.modelItemId,
      )) &&
      modelItem.calculationFormula
    ) {
      // if model item has specific calculation
      calcInfo.formula = modelItem.calculationFormula;
      calcInfo.parameters = modelItem.parameters;
    } else {
      if (!estimationItem.methodologyId) {
        // The item has no model-item formula and no methodology reference. This can happen
        // when a methodology was deleted or when the estimation item's methodologyId is
        // missing due to a data inconsistency (see EWM-597 duplicate methodology issue).
        // Log a warning and treat the item as unestimated rather than crashing the tab.
        // eslint-disable-next-line no-console
        console.warn(
          `[EstimationCalculator] No methodologyId on estimation item "${estimationItem.name}" (id: ${estimationItem.id}). ` +
            'Item will be treated as unestimated (value = 0). ' +
            'Reassign a valid methodology to this item to restore its estimate.',
        );
        return calcInfo; // formula stays null → calculateEstimationByFormula returns 0
      }
      // else use methodology
      const methodology = this.estimationMethodologies?.find(
        (m) => m.id === estimationItem.methodologyId,
      );
      if (!methodology) {
        // The methodologyId is set but the methodology was not found in the loaded collection.
        // This happens when the item references a methodology that is not included in the
        // estimation model's methodologyIds (e.g. after a duplicate-methodology cleanup, EWM-597).
        // Log a warning and treat the item as unestimated rather than crashing the tab.
        // eslint-disable-next-line no-console
        console.warn(
          `[EstimationCalculator] Methodology id "${estimationItem.methodologyId}" not found for estimation item "${estimationItem.name}" (id: ${estimationItem.id}). ` +
            'Item will be treated as unestimated (value = 0). ' +
            'Reassign a valid methodology to this item to restore its estimate.',
        );
        return calcInfo; // formula stays null → calculateEstimationByFormula returns 0
      }
      calcInfo.formula = methodology.calculationFormula ?? null;
      calcInfo.parameters = methodology.defaultParameters;
    }

    // modify formula for evaluator - NcalcJS requires [] arround parameters
    if (calcInfo.formula) {
      // manually find and replace patterns to avoid any regex edge cases
      let formula = calcInfo.formula;

      // Find all parameter patterns like "param[ref]" that aren't already wrapped
      const paramPattern = /[a-zA-Z]+\[[a-zA-Z0-9_-]+\]/g;
      const matches = formula.match(paramPattern);

      if (matches) {
        // Process each unique match only once
        const uniqueMatches = [...new Set(matches)];
        uniqueMatches.forEach((match) => {
          // Only replace if the match isn't already wrapped (not preceded by [)
          const wrappedVersion = `[${match}]`;
          if (!formula.includes(wrappedVersion)) {
            formula = formula.replaceAll(match, wrappedVersion);
          }
        });
      }

      calcInfo.formula = formula;
    }

    return structuredClone(calcInfo);
  }

  private calculateSiblingsEstimations(
    estimationItem: EstimationItemForCalculation,
    estimationItems: EstimationItemForCalculation[],
    skipItems: string[] | null = null,
    recursiveVisits: Set<string>,
  ) {
    return estimationItems.reduce((sum, item) => {
      if (item === estimationItem) return sum;

      if (item.calculationRef && (skipItems?.indexOf(item.calculationRef) ?? -1) >= 0) return sum;

      // only items on the same subtree level (same parent)
      if (item.parentId !== estimationItem.parentId) return sum;

      return sum + this.calculateItemEstimation(item, estimationItems, recursiveVisits);
    }, 0);
  }
}
